/**
 * Zod schemas for the Agent runs panel API (#169 slice 3).
 *
 * Mirrors the panel models in `backend/features/agent_runs/schemas.py`
 * field for field (PanelResponse, PanelSetup, DefinitionSummary, DefinitionDetail,
 * DefinitionRunRow, and the editor's Preview/Render/Save/Withdraw bodies).
 * The backend sends every field, defaults included, so nothing here is
 * optional: a field it may leave empty is `.nullable()`.
 */

import { z } from 'zod';
import { RunStatusSchema } from './agentRun.schemas';

export const PanelAccessSchema = z.enum(['connect', 'read', 'write']);
export const DefinitionLintSchema = z.enum(['ok', 'warning', 'error']);
/** A rename is a removal at the old name plus a proposed new one. */
export const MrChangeSchema = z.enum(['new', 'modified', 'deleted']);
export const GateStatusSchema = z.enum(['green', 'red', 'running', 'unknown']);

export const LintFindingSchema = z.object({
  path: z.string(),
  severity: z.enum(['error', 'warning']),
  message: z.string(),
});

export const MrRefSchema = z.object({
  iid: z.number(),
  url: z.string(),
  author: z.string(),
  change: MrChangeSchema,
});

export const LastRunSchema = z.object({
  run_id: z.string(),
  status: RunStatusSchema,
  mr_url: z.string().nullable(),
  created_at: z.string(),
});

export const DefinitionSummarySchema = z.object({
  /** The file stem, so a broken file still has a name. */
  name: z.string(),
  path: z.string(),
  /** `<stem>.yaml` / `.yml`: unique among entries; the detail route takes it. */
  file_name: z.string(),
  /** `on.job_failed`; empty when the file does not parse. */
  trigger: z.array(z.string()),
  lint: DefinitionLintSchema,
  findings: z.array(LintFindingSchema),
  /** The most recently updated open MR changing this file. */
  pending_mr: MrRefSchema.nullable(),
  last_run: LastRunSchema.nullable(),
});

/**
 * The project's setup steps (owner, 2026-11-03): the harness assigned by a
 * Sanctum admin, and the default branch's CI including the agent
 * component. `ci`: "wired"; "not_wired"; "named" (the file names the
 * component but GitLab's lint doesn't run it, its rules, say); "invalid"
 * (the configuration doesn't validate); "missing" (no `.gitlab-ci.yml`);
 * "unknown" when GitLab could not say (`ci_detail` says why). `ci_mr` is
 * Sanctum's open CI-wiring MR (looked up only while not wired or invalid);
 * `include_line` the `- component: …` line, null when this Sanctum
 * publishes no component. A PUT .../paused answer has no setup.
 */
export const CiWiringSchema = z.enum(['wired', 'not_wired', 'named', 'invalid', 'missing', 'unknown']);

export const PanelSetupSchema = z.object({
  harness_assigned: z.boolean(),
  harness_active: z.boolean(),
  ci: CiWiringSchema,
  ci_detail: z.string().nullable(),
  ci_mr: MrRefSchema.nullable(),
  include_line: z.string().nullable(),
});

/**
 * Under `access == "connect"` only `access` carries meaning: the rest are
 * defaults, `project_path` and `setup` are null (GitLab has not vouched for
 * the viewer). `paused_by` is a GitLab username.
 */
export const PanelResponseSchema = z.object({
  access: PanelAccessSchema,
  project_path: z.string().nullable(),
  read_only_reason: z.string().nullable(),
  default_branch: z.string().nullable(),
  web_url: z.string().nullable(),
  paused: z.boolean(),
  paused_by: z.string().nullable(),
  paused_at: z.string().nullable(),
  can_pause: z.boolean(),
  can_resume: z.boolean(),
  setup: PanelSetupSchema.nullable(),
  definitions: z.array(DefinitionSummarySchema),
  proposed: z.array(DefinitionSummarySchema),
  /** More than 50 open MRs: only the 50 most recently updated were checked. */
  proposed_truncated: z.boolean(),
  /** Open MRs whose changes could not be read; any may touch a definition. */
  unchecked_mr_iids: z.array(z.number()),
  /** Why the lists are empty although the project's get succeeded. */
  list_error: z.string().nullable(),
});

export const MrDetailSchema = z.object({
  iid: z.number(),
  url: z.string(),
  title: z.string(),
  author: z.string(),
  source_branch: z.string(),
  pipeline_status: z.string().nullable(),
  /** Null when GitLab doesn't answer (an edition without approvals). */
  approvals_given: z.number().nullable(),
  approvals_required: z.number().nullable(),
  /** GitLab's unified diff of this file: render as text, never HTML. */
  diff: z.string(),
  is_sanctum_branch: z.boolean(),
});

export const DefinitionDetailSchema = z.object({
  summary: DefinitionSummarySchema,
  state: z.enum(['enabled', 'proposed']),
  content: z.string().nullable(),
  /** The editor starts from this when `sanctum_mr` is set, else `content`. */
  mr_content: z.string().nullable(),
  mr: MrDetailSchema.nullable(),
  /** Sanctum's own open `sanctum-def/<name>` MR, which Withdraw closes. */
  sanctum_mr: MrRefSchema.nullable(),
  can_edit: z.boolean(),
  can_withdraw: z.boolean(),
});

const runRowFields = {
  id: z.string(),
  created_at: z.string(),
  ref: z.string(),
  pipeline_id: z.number().nullable(),
  pipeline_url: z.string().nullable(),
  mr_url: z.string().nullable(),
  /** The agent report's summary, at most 300 characters. */
  summary: z.string().nullable(),
  reason: z.string().nullable(),
  /** The pipeline user's GitLab username; null on rows from before it. */
  requested_by: z.string().nullable(),
  /** Looked up for the 20 most recent runs with an MR; null otherwise. */
  gate: GateStatusSchema.nullable(),
  /** The run's own fix MR's GitLab state, when its gate was read. */
  mr_state: z.string().nullable(),
  /** The agent changed nothing (backend `definition_runs._no_change`). */
  no_change: z.boolean(),
};

/**
 * A run always has a status and a match never does (backend
 * `definition_runs._run_row` / `_match_row`), so the row is split on `kind`.
 */
export const DefinitionRunRowSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('run'), status: RunStatusSchema, ...runRowFields }),
  z.object({ kind: z.literal('match'), status: z.null(), ...runRowFields }),
]);

export const DefinitionRunsResponseSchema = z.object({
  rows: z.array(DefinitionRunRowSchema),
});

/**
 * A run definition as the form edits it: preview's `definition`
 * (`RunDefinition.model_dump(mode="json")`, defaults filled) and render's
 * input. The backend validates the bounds; these are the shapes.
 */
export const BranchCategorySchema = z.enum(['default', 'protected', 'feature', 'merge_request']);

export const RunDefinitionSchema = z.object({
  name: z.string(),
  on: z.object({
    job_failed: z.array(z.string()),
    branches: z.array(BranchCategorySchema).optional(),
  }),
  priority: z.number().optional(),
  kind: z.literal('fix').optional(),
  agent: z.enum(['pi', 'claude-code']).nullable().optional(),
  model: z.string().nullable().optional(),
  instructions: z.string().nullable().optional(),
  input: z
    .object({
      report: z.object({ path: z.string(), format: z.literal('grype') }).nullable().optional(),
    })
    .nullable()
    .optional(),
  budget: z.object({ minutes: z.number(), tokens: z.number() }).nullable().optional(),
});

export const PreviewResponseSchema = z.object({
  /** Null when the text is no valid definition (the form is disabled). */
  definition: RunDefinitionSchema.nullable(),
  findings: z.array(LintFindingSchema),
});

export const RenderResponseSchema = z.object({ content: z.string() });

/** Sanctum's CI-wiring MR; `created` false when it was already open. */
export const CiIncludeResponseSchema = z.object({
  mr_iid: z.number(),
  mr_url: z.string(),
  created: z.boolean(),
});

export const SaveResponseSchema = z.object({
  mr_iid: z.number(),
  mr_url: z.string(),
  /** Save opened the MR (else it pushed to the open one). */
  created: z.boolean(),
});

export const WithdrawResponseSchema = z.object({
  /** The MR the backend closed: the one the request named. */
  mr_iid: z.number(),
  branch_deleted: z.boolean(),
  /** Why the branch was left in place. */
  reason: z.string().nullable(),
});

/** Save's lint 422: `detail = {message, findings}`. */
export const LintErrorDetailSchema = z.object({
  message: z.string(),
  findings: z.array(LintFindingSchema),
});

export type PanelAccess = z.infer<typeof PanelAccessSchema>;
export type DefinitionLint = z.infer<typeof DefinitionLintSchema>;
export type MrChange = z.infer<typeof MrChangeSchema>;
export type GateStatus = z.infer<typeof GateStatusSchema>;
export type LintFinding = z.infer<typeof LintFindingSchema>;
export type MrRef = z.infer<typeof MrRefSchema>;
export type LastRun = z.infer<typeof LastRunSchema>;
export type DefinitionSummary = z.infer<typeof DefinitionSummarySchema>;
export type PanelResponse = z.infer<typeof PanelResponseSchema>;
export type MrDetail = z.infer<typeof MrDetailSchema>;
export type DefinitionDetail = z.infer<typeof DefinitionDetailSchema>;
export type DefinitionRunRow = z.infer<typeof DefinitionRunRowSchema>;
export type BranchCategory = z.infer<typeof BranchCategorySchema>;
export type RunDefinition = z.infer<typeof RunDefinitionSchema>;
export type PreviewResponse = z.infer<typeof PreviewResponseSchema>;
export type SaveResponse = z.infer<typeof SaveResponseSchema>;
export type CiWiring = z.infer<typeof CiWiringSchema>;
export type PanelSetup = z.infer<typeof PanelSetupSchema>;
export type CiIncludeResponse = z.infer<typeof CiIncludeResponseSchema>;
export type WithdrawResponse = z.infer<typeof WithdrawResponseSchema>;
